import React, { useEffect } from 'react';

import { Box, Flex, Grid, Text, Skeleton, Button } from '@vimeo/bokeh';
import {
  AdminUser,
  CameraOn,
  MiscTools,
  ShoppingCart,
  WatchPlay,
  ChevronRightSmall,
  GameStructure,
  Code,
  Copyright,
} from '@vimeo/bokeh-icons';

import { vimeoHCClient } from '../api/vimeoHC';
import { useHelpPanel } from '../hooks/useHelpPanel';
import { useVimeoHC } from '../hooks/useVimeoHC';
import { HelpCategory, HelpArticle } from '../types';
import { useTrackHelpPanelEvent } from '../utils/bigPicture';

const categoryIconMap: { [key: string]: React.ReactElement } = {
  'account and billing': <AdminUser />,
  'watching videos and events': <WatchPlay />,
  'making, managing, and sharing videos': <GameStructure />,
  'virtual events and livestreaming': <CameraOn />,
  'monetize content': <ShoppingCart />,
  'integrations and third-party apps': <MiscTools />,
  'policy, safety, and copyright': <Copyright />,
  developers: <Code />,
};

const getCategoryIcon = (title: string): React.ReactElement => {
  const lowerCaseTitle = title.toLowerCase();

  for (const key in categoryIconMap) {
    if (lowerCaseTitle.includes(key)) {
      return categoryIconMap[key];
    }
  }

  // Default icon
  return <AdminUser />;
};

export const HomePanel: React.FC = () => {
  const { goToSection, goToArticle, goToPage, data: contextData } = useHelpPanel();
  const { loading, error, execute } = useVimeoHC(vimeoHCClient.getHomeData);
  const trackHelpPanelEvent = useTrackHelpPanelEvent();

  useEffect(() => {
    if (!contextData.home) {
      execute().then((result) => {
        if (result) {
          goToPage('home', result);
        }
      });
    }
  }, [contextData.home, execute, goToPage]);

  if (loading || !contextData.home) {
    return (
      <Box gap={4} width="100%">
        <Skeleton height="md" borderRadius="md" width="100%" />
        <Flex wrap="wrap" gap={4} mt="xs">
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} height="112px" borderRadius="md" width="47.5%" />
          ))}
        </Flex>
        <Flex direction="column" gap="sm" mt="xs">
          {[...Array(2)].map((_, index) => (
            <Skeleton key={index} height="8px" borderRadius="sm" width="100%" />
          ))}
          <Skeleton height="8px" borderRadius="sm" width="68%" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} color="status-destructive-primary">
        Failed to load help content. Please try again.
      </Box>
    );
  }

  const { categories, featured } = contextData.home;

  return (
    <Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={3} p="3px">
        {categories.map((category: HelpCategory) => (
          <Flex
            as={Button}
            key={category.id}
            variant="tertiary"
            direction="column"
            justifyContent="space-between"
            height="112px"
            p={4}
            alignItems="flex-start"
            border="1px solid"
            borderColor="var(--vimeo-colors-stroke)"
            width="100%"
            onClick={() => {
              goToSection(category);
              trackHelpPanelEvent({
                eventName: 'vimeo.in_app_support_panel_button_click',
                element: 'button',
                copy: category.title,
              });
            }}
          >
            {getCategoryIcon(category.title)}
            <Text as="p" variant="body-md" whiteSpace="normal" textAlign="left">
              {category.title}
            </Text>
          </Flex>
        ))}
      </Grid>
      <Flex direction="column" gap={1} mt={5}>
        <Text variant="heading-xs" mb={8}>
          Featured articles
        </Text>
        {featured.map((article: HelpArticle) => (
          <Flex
            as={Button}
            key={article.id}
            variant="tertiary"
            p="sm"
            height="100%"
            borderRadius="md"
            alignItems="flex-start"
            onClick={() => {
              goToArticle(article);
              trackHelpPanelEvent({
                eventName: 'vimeo.in_app_support_panel_button_click',
                element: 'button',
                copy: 'featured articles',
              });
            }}
          >
            <Text
              flex="1"
              variant="body-md"
              textAlign="left"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              minWidth="0"
            >
              {article.title}
            </Text>
            <ChevronRightSmall />
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};
